import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useEpic, useEpicWrapUp, useUpdateEpic, useDeleteEpic, useArchiveEpic, useUnarchiveEpic } from "@/hooks/queries/use-epics";
import { IssuesList } from "@/components/issues/issues-list";
import { SplitDetailPanel } from "@/components/issues/split-detail-panel";
import { usePageTitle } from "@/hooks/use-page-title";
import { useBreakpoint } from "@/hooks/use-mobile";
import { KanbanBoard } from "@/components/issues/kanban-board";
import { useIssues, type IssueItem } from "@/hooks/queries/use-issues";
import { useActiveSession } from "@/hooks/queries/use-active-session";
import { getTeamHueColor } from "@/lib/team-hue";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";
import { OverlayPanel } from "@/components/ui/overlay-panel";
import { ComplianceDetailSection } from "@/components/ui/compliance-detail-section";
import { useAuditEpic } from "@/hooks/queries/use-compliance";

import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Check, X, MoreHorizontal, ChevronDown, ChevronRight, Archive, ArchiveRestore, FileText, ExternalLink, AlertTriangle, Clock, List, LayoutGrid, ArrowLeft, User, Laptop } from "lucide-react";
import { CommandCopyBox } from "@/components/common/command-copy-box";
import { RunOnMachineDialog } from "@/components/epic-requests/run-on-machine-dialog";
import { ActiveJobCancel } from "@/components/mailbox/active-job-cancel";
import { EpicPullRequestStatus } from "@/components/epics/epic-pull-request-status";
import { WaitingPullRequests } from "@/components/mailbox/waiting-pull-requests";
import { ReportGapDialog, canReportGap } from "@/components/epics/report-gap-dialog";
import { ModelRecommendationCard } from "@/components/epics/model-recommendation-card";
import { ImprovePlanDialog, canImprovePlan } from "@/components/epics/improve-plan-dialog";
import { PlanNeedsReviewBanner } from "@/components/epics/plan-needs-review-banner";
import { EpicWrapUpBannerView, type WrapUpGapReport } from "@/components/epics/epic-wrap-up-banner";
import { PlanView } from "@/components/execution-plan";
import { SessionMonitor } from "@/components/session";
import { ActivityFeed } from "@/components/epics/activity-feed";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/lib/get-initials";
import {
  buildOrchestratorCommand,
  COPILOT_PROMPT_EXPLANATION,
} from "@/lib/chat/cli-guidance";
import { HELP_IDS } from "@/lib/help-ids";
import { useIdeaHref } from "@/hooks/use-new-look";

export function EpicDetailPage() {
  const ideaHref = useIdeaHref();
  const { epicId } = useParams<{ epicId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: epic, isLoading } = useEpic(epicId ?? "");
  // ENG-329: the build's wrap-up, shared by the banner and the PR status.
  const { data: wrapUp } = useEpicWrapUp(epic?.id ?? "");
  usePageTitle(epic?.name ?? "Epic");
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';
  const isDesktop = breakpoint === 'desktop';
  const updateEpic = useUpdateEpic();
  const deleteEpic = useDeleteEpic();
  const archiveEpic = useArchiveEpic();
  const unarchiveEpic = useUnarchiveEpic();
  const auditEpic = useAuditEpic();


  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [glowExiting, setGlowExiting] = useState(false);

  const handleBack = useCallback(() => {
    setGlowExiting(true);
    setTimeout(() => void navigate("/epics"), 400);
  }, [navigate]);
  
  // Initialize active tab from URL params or default to 'overview'
  const initialTab = (searchParams.get('tab') as 'overview' | 'plan' | 'monitor' | 'activity') || 'overview';
  const [activeTab, setActiveTab] = useState<'overview' | 'plan' | 'monitor' | 'activity'>(initialTab);

  // Split-pane + kanban state
  const [selectedItem, setSelectedItem] = useState<IssueItem | null>(null);
  useLockBodyScroll(selectedItem !== null);

  // On mobile, navigate to feature/task page instead of opening overlay
  const handleItemSelect = (item: IssueItem) => {
    if (isMobile) {
      const path = item.type === 'feature'
        ? `/features/${item.data.id}`
        : `/tasks/${item.data.id}`;
      void navigate(path);
    } else {
      setSelectedItem(item);
    }
  };

  const [viewMode, setViewMode] = useState<'list' | 'board'>(() => {
    return (localStorage.getItem('epicViewMode') as 'list' | 'board') || 'list';
  });
  const { hasActiveSession } = useActiveSession(epic?.id);
  const { data: allIssues = [] } = useIssues({
    epicId: epic?.id ?? "",
    // Poll every 5s during active implementation to catch updates
    // that SSE may miss in multi-instance deployments
    refetchInterval: hasActiveSession ? 5_000 : false,
  });
  const epicFeatures = allIssues.flatMap((item) => (item.type === "feature" ? [item.data] : []));
  // ENG-355: the report-gap dialog can be opened from the wrap-up banner, pre-filled.
  const [reportGapOpen, setReportGapOpen] = useState(false);
  const [gapReport, setGapReport] = useState<WrapUpGapReport | null>(null);

  // Update URL when tab changes (for deep linking and persistence)
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    if (currentTab !== activeTab) {
      const newParams = new URLSearchParams(searchParams);
      if (activeTab === 'overview') {
        // Remove tab param for overview (default)
        newParams.delete('tab');
      } else {
        newParams.set('tab', activeTab);
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // On desktop the Activity sidebar is always visible, so treat the tab as overview
  const effectiveTab = (isDesktop && activeTab === 'activity') ? 'overview' : activeTab;

  // All hooks must be called before any early returns
  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-6 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (!epic) {
    return (
      <div className="pt-4">
        <p className="text-muted-foreground">Epic not found</p>
        <Button variant="ghost" onClick={handleBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to epics
        </Button>
      </div>
    );
  }

  const handleNameSave = async () => {
    if (editedName.trim() && editedName !== epic.name) {
      await updateEpic.mutateAsync({
        id: epic.id,
        name: editedName.trim(),
      });
    }
    setIsEditingName(false);
  };

  const handleDelete = async () => {
    await deleteEpic.mutateAsync(epic.id);
    navigate("/epics");
  };

  const handleArchive = async () => {
    await archiveEpic.mutateAsync(epic.id);
    setShowArchiveDialog(false);
    navigate("/epics");
  };

  const handleUnarchive = async () => {
    await unarchiveEpic.mutateAsync(epic.id);
  };

  const hueColor = getTeamHueColor(epic.team?.name);
  const orchestratorCommand = buildOrchestratorCommand(epic.identifier ?? epic.name);

  return (
    <div className="relative">
      {/* Background hue glow — Fey style soft ambient */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `radial-gradient(ellipse 140% 50% at 50% -5%, ${hueColor}2B, transparent 70%)`,
          backgroundColor: `${hueColor}09`,
          animation: `${glowExiting ? 'glow-exit' : 'glow-enter'} 0.5s ease-out forwards`,
        }}
      />

      <div className="relative z-10 pt-4 space-y-4">
        {/* ── Top card: Header + description + command ── */}
        <div className="glass-card rounded-2xl overflow-hidden">
          {/* ENG-290: critical-fail plan whose Epic Request can't convert yet */}
          <PlanNeedsReviewBanner epic={epic} />
          {/* ENG-329: where the build's wrap-up stands */}
          {wrapUp && (
            <EpicWrapUpBannerView
              epicId={epic.id}
              view={wrapUp}
              features={epicFeatures}
              // ENG-355: a failed wrap-up opens the report-gap dialog pre-filled.
              {...(canReportGap(epic)
                ? { onReportGap: (report: WrapUpGapReport) => { setGapReport(report); setReportGapOpen(true); } }
                : {})}
            />
          )}
          {/* Header row */}
          <div className="flex items-center gap-3 px-4 md:px-6 py-4">
            <div className="flex-1 min-w-0">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="text-lg font-semibold h-8"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleNameSave();
                      if (e.key === "Escape") setIsEditingName(false);
                    }}
                  />
                  <Button size="icon" variant="ghost" className="h-11 w-11 md:h-8 md:w-8" onClick={handleNameSave}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-11 w-11 md:h-8 md:w-8" onClick={() => setIsEditingName(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {epic.identifier && (
                    <span className="text-sm text-muted-foreground font-mono">{epic.identifier}</span>
                  )}
                  <h1
                    className="text-xl md:text-lg font-semibold truncate cursor-pointer hover:text-muted-foreground transition-colors"
                    data-help-id={HELP_IDS.epic.header.title}
                    onClick={() => {
                      if (!epic.isArchived) {
                        setEditedName(epic.name);
                        setIsEditingName(true);
                      }
                    }}
                  >
                    {epic.name}
                  </h1>
                  {epic.isArchived && (
                    <Badge variant="secondary" className="gap-1">
                      <Archive className="h-3 w-3" />
                      Archived
                    </Badge>
                  )}
                  {!epic.team && (
                    <Badge variant="outline" className="gap-1 text-muted-foreground">
                      <User className="h-3 w-3" />
                      Personal
                    </Badge>
                  )}
                  {!isMobile && epic.creator && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-2 pl-2 border-l border-white/10">
                      <Avatar className="h-5 w-5">
                        {epic.creator.avatarUrl && <AvatarImage src={epic.creator.avatarUrl} alt={epic.creator.name} />}
                        <AvatarFallback className="text-xs">
                          {getInitials(epic.creator.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate max-w-[120px]">{epic.creator.name}</span>
                      <span>•</span>
                      <span>{new Date(epic.createdAt).toLocaleDateString()}</span>
                    </div>
                  )}
                  {!isMobile && epic.implementer && epic.implementedDate && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-2 pl-2 border-l border-white/10">
                      <Check className="h-3 w-3 text-green-500" />
                      <Avatar className="h-5 w-5">
                        {epic.implementer.avatarUrl && <AvatarImage src={epic.implementer.avatarUrl} alt={epic.implementer.name} />}
                        <AvatarFallback className="text-xs">
                          {getInitials(epic.implementer.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate max-w-[120px]">{epic.implementer.name}</span>
                      <span>•</span>
                      <span>{new Date(epic.implementedDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {!isMobile && (() => {
                    const sourceRequest = epic.convertedFromRequests?.[0];
                    if (!sourceRequest) return null;
                    return (
                      <Link
                        to={ideaHref(sourceRequest.id)}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground ml-2 pl-2 border-l border-white/10 hover:text-foreground transition-colors"
                      >
                        <FileText className="h-3 w-3 text-blue-400" />
                        <span>
                          Created from{" "}
                          <span className="font-mono text-blue-400">
                            {sourceRequest.identifier ?? "Epic Request"}
                          </span>
                        </span>
                      </Link>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {epic.isArchived && (
                <Button size="sm" variant="outline" onClick={handleUnarchive} disabled={unarchiveEpic.isPending}>
                  <ArchiveRestore className="h-4 w-4 mr-1.5" />
                  {unarchiveEpic.isPending ? "Restoring..." : "Restore Epic"}
                </Button>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8" data-help-id={HELP_IDS.epic.header.moreMenu}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {epic.isArchived ? (
                    <>
                      <DropdownMenuItem onClick={handleUnarchive}>
                        <ArchiveRestore className="h-4 w-4 mr-2" />
                        Restore Epic
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Permanently
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>


                      <DropdownMenuItem onClick={() => setShowArchiveDialog(true)}>
                        <Archive className="h-4 w-4 mr-2" />
                        Archive Epic
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Epic
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Collapsible Description */}
          {(epic.description || epic.structuredDesc) && (
            <div className="border-t border-white/[0.06]">
              <button
                className="flex items-center gap-2 w-full px-4 md:px-6 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                data-help-id={HELP_IDS.epic.description.toggle}
              >
                {isDescriptionExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                <span>Epic Description</span>
                {epic.structuredDesc?.riskLevel && (
                  <Badge variant={epic.structuredDesc.riskLevel === "high" ? "destructive" : epic.structuredDesc.riskLevel === "medium" ? "secondary" : "outline"} className="ml-2 text-xs hidden md:inline-flex">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {epic.structuredDesc.riskLevel} risk
                  </Badge>
                )}
                {epic.structuredDesc?.estimatedEffort && (
                  <Badge variant="outline" className="ml-1 text-xs hidden md:inline-flex">
                    <Clock className="h-3 w-3 mr-1" />
                    {epic.structuredDesc.estimatedEffort}
                  </Badge>
                )}
              </button>
              {!isDescriptionExpanded && epic.structuredDesc?.summary && (
                <div className="px-4 md:px-6 pb-3">
                  <p className="text-sm text-muted-foreground line-clamp-2">{epic.structuredDesc.summary}</p>
                </div>
              )}
              {isDescriptionExpanded && (
                <div className="px-4 md:px-6 pb-4 space-y-3" data-help-id={HELP_IDS.epic.description.content}>
                  {epic.description && (
                    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                      <MarkdownRenderer content={epic.description} className="text-sm" />
                    </div>
                  )}
                  {epic.structuredDesc?.summary && !epic.description && (
                    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                      <MarkdownRenderer content={epic.structuredDesc.summary} className="text-sm" />
                    </div>
                  )}
                  {epic.structuredDesc?.aiInstructions && (
                    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">AI Instructions</h4>
                      <MarkdownRenderer content={epic.structuredDesc.aiInstructions} className="text-sm" />
                    </div>
                  )}
                  {epic.structuredDesc?.acceptanceCriteria && epic.structuredDesc.acceptanceCriteria.length > 0 && (
                    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Acceptance Criteria</h4>
                      <ul className="space-y-1">
                        {epic.structuredDesc.acceptanceCriteria.map((criterion, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <span className="text-muted-foreground mt-0.5">&#9744;</span>
                            <span>{criterion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {epic.structuredDesc?.filesInvolved && epic.structuredDesc.filesInvolved.length > 0 && (
                    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                        <FileText className="h-3 w-3 inline mr-1" />
                        Files Involved
                      </h4>
                      <ul className="space-y-0.5">
                        {epic.structuredDesc.filesInvolved.map((file, i) => (
                          <li key={i} className="text-sm font-mono text-muted-foreground">{file}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {epic.structuredDesc?.technicalNotes && (
                    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Technical Notes</h4>
                      <MarkdownRenderer content={epic.structuredDesc.technicalNotes} className="text-sm" />
                    </div>
                  )}
                  {epic.structuredDesc?.externalLinks && epic.structuredDesc.externalLinks.length > 0 && (
                    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                        <ExternalLink className="h-3 w-3 inline mr-1" />
                        External Links
                      </h4>
                      <ul className="space-y-1">
                        {epic.structuredDesc.externalLinks.map((link, i) => (
                          <li key={i}>
                            <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                              {link.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Compliance Section */}
          <div className="border-t border-white/[0.06] px-4 md:px-6 py-3">
            <ComplianceDetailSection
              score={epic.complianceScore}
              onReaudit={() => { void auditEpic.mutateAsync(epic.id); }}
              isAuditing={auditEpic.isPending}
            />
            {/* ENG-290: send the audit feedback back to the planner. Hidden
                when the plan passes (or hasn't been audited). */}
            {canImprovePlan(epic.complianceScore) && (
              <div className="mt-3">
                <ImprovePlanDialog
                  epicId={epic.id}
                  targetRemote={epic.team?.gitRemote ?? null}
                  open={searchParams.get("improve") === "1"}
                  onOpenChange={(next) => {
                    const params = new URLSearchParams(searchParams);
                    if (next) params.set("improve", "1");
                    else params.delete("improve");
                    setSearchParams(params, { replace: true });
                  }}
                />
              </div>
            )}
          </div>

          {/* Model Recommendation */}
          {epic.buildPullRequest && (
            <div className="border-t border-white/[0.06] px-4 md:px-6 py-3">
              <EpicPullRequestStatus epic={epic} wrapUp={wrapUp} />
            </div>
          )}
          {/* ER-130: every PR the epic's runs opened, in merge order, with links and CI. */}
          <WaitingPullRequests epicId={epic.id} />

          {/* ENG-328: report what an implemented epic is missing. */}
          {canReportGap(epic) && (
            <div className="border-t border-white/[0.06] px-4 md:px-6 py-3">
              <ReportGapDialog
                epic={epic}
                features={epicFeatures}
                open={reportGapOpen}
                onOpenChange={(next) => {
                  setReportGapOpen(next);
                  if (!next) setGapReport(null);
                }}
                initialFeatureId={gapReport?.featureId ?? null}
                initialDescription={gapReport?.summary ?? null}
              />
            </div>
          )}

          {(epic.recommendedModel || epic.actualModelUsed) && (
            <div className="border-t border-white/[0.06] px-4 md:px-6 py-3">
              <ModelRecommendationCard epic={epic} />
            </div>
          )}

          {/* Orchestrator command — shown on all breakpoints (ENG-163 dispatch
              must be reachable on mobile, where there is no terminal at all). */}
          {!epic.isArchived && (
            <div className="border-t border-white/[0.06] px-4 md:px-6 py-3">
              <CommandCopyBox
                label="Execute this Epic"
                command={orchestratorCommand}
                helperText={COPILOT_PROMPT_EXPLANATION}
                commandHelpId={HELP_IDS.epic.execute.command}
                copyButtonHelpId={HELP_IDS.epic.execute.copyButton}
              />
              {/* Send-to-my-machine (ENG-163): dispatch the orchestrator to a
                  paired machine — no terminal needed — as a
                  start_execution/orchestrate job. */}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">or run it without a terminal:</span>
                <RunOnMachineDialog
                  jobType="start_execution"
                  payload={{ kind: "orchestrate", epicId: epic.id }}
                  targetRemote={epic.team?.gitRemote ?? null}
                  defaultFastMode={epic.team?.fastMode ?? false}
                  defaultCheckMode={epic.team?.checkMode}
                >
                  <Laptop className="h-4 w-4 mr-2" />
                  Send to my machine
                </RunOnMachineDialog>
              </div>
              <div className="mt-3">
                <ActiveJobCancel query={{ epicId: epic.id }} label="A build" />
              </div>
            </div>
          )}
        </div>

        {/* ── Main content + Activity feed row ── */}
        <div className="flex gap-4 items-stretch">
          {/* ── Main content card: Tabs + issues/kanban ── */}
          <div className="glass-card rounded-2xl overflow-hidden flex-1 min-w-0">
          {/* Tab bar with view toggle — scrollable pill tabs on mobile */}
          <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-white/[0.06]">
            <div className={`flex items-center gap-1 ${isMobile ? 'overflow-x-auto scrollbar-none -mx-1 px-1' : ''}`} data-help-id={HELP_IDS.epic.tabs.container}>
              {(['overview', 'plan', 'monitor', 'activity'] as const).map((tab) => {
                // Activity tab is only for mobile/tablet — desktop has the sidebar
                if (tab === 'activity' && isDesktop) return null;
                return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  data-help-id={HELP_IDS.epic.tabs[tab]}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors whitespace-nowrap ${
                    effectiveTab === tab
                      ? 'bg-white/[0.08] text-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]'
                  }`}
                >
                  {tab === 'overview' ? 'Overview' : tab === 'plan' ? 'Plan' : tab === 'activity' ? 'Activity' : isMobile ? 'Monitor' : 'Session Monitor'}
                </button>
                );
              })}
            </div>

            {effectiveTab === 'overview' && !isMobile && (
              <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-0.5">
                <button
                  onClick={() => { setViewMode('list'); localStorage.setItem('epicViewMode', 'list'); }}
                  data-help-id={HELP_IDS.epic.viewToggle.list}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'list' ? 'bg-white/[0.08] text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <List className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => { setViewMode('board'); localStorage.setItem('epicViewMode', 'board'); }}
                  data-help-id={HELP_IDS.epic.viewToggle.board}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'board' ? 'bg-white/[0.08] text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Tab content */}
          <div className={`min-h-[400px] ${(effectiveTab === 'monitor') ? `flex flex-col flex-1 ${isMobile ? 'h-[calc(100vh-280px)]' : 'h-[calc(100vh-220px)]'}` : (effectiveTab === 'activity') ? `flex flex-col flex-1 h-[calc(100vh-280px)]` : ''}`}>
            {effectiveTab === 'overview' && (
              <div className="min-h-[400px]" data-help-id={HELP_IDS.epic.featureList}>
                {!isMobile && viewMode === 'board' ? (
                  <KanbanBoard
                    issues={allIssues}
                    onItemSelect={handleItemSelect}
                    selectedItemId={selectedItem?.data.id}
                  />
                ) : (
                  epic?.id && (
                    <IssuesList
                      epicId={epic.id}
                      onItemSelect={handleItemSelect}
                      selectedItemId={selectedItem?.data.id}
                    />
                  )
                )}
              </div>
            )}

            {effectiveTab === 'plan' && epic?.id && (
              <div className="p-4">
                <PlanView epicId={epic.id} />
              </div>
            )}

            {effectiveTab === 'monitor' && epic?.id && (
              <div className="p-4 flex-1 min-h-0 flex flex-col">
                <SessionMonitor epicId={epic.id} className="flex-1 min-h-0" />
              </div>
            )}

            {/* Activity tab only renders on mobile/tablet — desktop uses the sidebar */}
            {effectiveTab === 'activity' && epic?.id && (
              <div className="flex-1 min-h-0 flex flex-col">
                <ActivityFeed epicId={epic.id} className="flex-1 min-h-0" />
              </div>
            )}
           </div>
         </div>

          {/* ── Floating Activity Feed card ── */}
          {epic?.id && (
            <div className="hidden lg:block w-[340px] shrink-0" data-help-id={HELP_IDS.epic.activityFeed}>
              <div className="glass-card rounded-2xl overflow-hidden sticky top-4" style={{ height: 'calc(100vh - 2rem)' }}>
                <ActivityFeed epicId={epic.id} className="h-full" />
              </div>
            </div>
          )}
        </div>
      </div>




      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Epic</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{epic.name}"? This will also
              delete all features in this epic. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteEpic.isPending}
            >
              {deleteEpic.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Epic</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive "{epic.name}"? The epic and its features
              will be hidden from the default view but can be restored later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleArchive}
              disabled={archiveEpic.isPending}
            >
              {archiveEpic.isPending ? "Archiving..." : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fixed right overlay detail panel — Fey style */}
      {selectedItem && (
        <OverlayPanel open={!!selectedItem} onClose={() => setSelectedItem(null)}>
          <SplitDetailPanel
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
          />
        </OverlayPanel>
      )}
    </div>
  );
}
